import {
  DesktopOutlined,
  EditOutlined,
  EllipsisOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  UserAddOutlined
} from '@ant-design/icons'
import {
  Button,
  Col,
  Drawer,
  Dropdown,
  message,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Input
} from 'antd'
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { JOIN_TYPE_HANDS_ON } from '~/utils/constants'
import { clipboard, compareNumber, compareText } from '~/utils/helpers'
import {
  useAppstreamsQuery,
  useCourseServersQuery
} from '../CourseServers/query'
import {
  useDeleteparticipantMutation,
  useResetPasskeyMutation,
  useStudentEnabledMutation,
  useStudentPreAccessMutation
} from './mutation'
import { courseParticipantQueryKey, useCourseParticipantsQuery } from './query'
import { ParticipantForm } from './StudentForm'

const { confirm } = Modal

interface Server {
  name: string
}

interface Appstream {
  stack_name: string
}

interface Participant {
  id: number
  first_name: string
  last_name: string
  email: string
  username?: string
  password?: string
  server_id: number
  enabled: boolean
  pre_access: boolean
  is_passkey_required: boolean
  has_passkey?: boolean
  passkey_provider?: string
  alias: string
  server?: Server
  appstream?: Appstream
  registration_cancelled_at: string | null
  avw_role: string | null
  reserve_server?: Server
  workstation_link: string
  join_type: number | null
  appstream_token?: string
}

const CourseParticipants = () => {
  const { id: courseId } = useParams()
  const id = Number(courseId)
  const { data, isLoading } = useCourseParticipantsQuery(id)
  const dataSource = useTableDataSourceKey<Participant>(data)
  const [filterInput, setFilterInput] = useState('')
  const [showDisabled, setShowDisabled] = useState(false)

  const { data: servers } = useCourseServersQuery(id)
  const { data: appstreams } = useAppstreamsQuery(id)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const { mutate: studentEnabled } = useStudentEnabledMutation(id)
  const { mutate: studentPreAccess } = useStudentPreAccessMutation(id)
  const { mutate: resetPasskey } = useResetPasskeyMutation(id)
  const queryClient = useQueryClient()
  const serverFilters = useMemo(() => {
    if (servers == null) {
      return []
    }

    const list = servers.map((i) => {
      return {
        text: i.name,
        value: i.name
      }
    })

    list.push({
      text: 'Unassigned',
      value: 'unassigned'
    })

    return list
  }, [servers])

  const appstreamFilters = useMemo(() => {
    if (appstreams == null) {
      return []
    }

    const list = appstreams.map((i) => {
      return {
        text: i.stack_name,
        value: i.stack_name
      }
    })

    list.push({
      text: 'Unassigned',
      value: 'unassigned'
    })

    return list
  }, [appstreams])

  const confirmEnabled = (item: Participant): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to ${
        item.enabled ? 'disable' : 'enable'
      } the student?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        studentEnabled(item.id)
      }
    })
  }

  const confirmPreAccess = (item: Participant): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to ${
        item.pre_access ? 'disable' : 'enable'
      } pre-access to the student?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        studentPreAccess(item.id)
      }
    })
  }

  const confirmResetPasskey = (item: Participant): void => {
    confirm({
      title: 'Confirm reset passkey',
      icon: <QuestionCircleOutlined />,
      content:
        'Do you want to reset the passkey for this workstation? This will require the user to register a new passkey on next access.',
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        resetPasskey(item.id)
      }
    })
  }

  const { mutate } = useDeleteparticipantMutation(id)
  const confirmDelete = (item: Participant): void => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to delete the student '${[
        item.first_name,
        item.last_name
      ].join(' ')}'?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        mutate(item.id)
      }
    })
  }

  const filterData = useMemo(() => {
    const filter = filterInput.toLowerCase()
    if (!Array.isArray(dataSource)) {
      return []
    }

    return dataSource.filter((record) => {
      const student = record as Participant
      if (!showDisabled && !student.enabled) {
        return false
      }
      return (
        `${student.first_name} ${student.last_name}`
          .toLowerCase()
          .includes(filter) || student.email.toLowerCase().includes(filter)
      )
    })
  }, [dataSource, filterInput, showDisabled])

  const participantCounts = useMemo(() => {
    if (!Array.isArray(dataSource)) {
      return {
        total: 0,
        active: 0,
        deactivated: 0
      }
    }

    const active = dataSource.filter((participant: Participant) => {
      return participant.enabled
    }).length

    return {
      total: dataSource.length,
      active,
      deactivated: dataSource.length - active
    }
  }, [dataSource])

  return (
    <>
      <PageHeader
        className='radup-page-header'
        extra={[
          <Col key={1}>
            <Row>
              <Space>
                <div>
                  <span>Show disabled workstations </span>
                  <Switch checked={showDisabled} onChange={setShowDisabled} />
                </div>
                <Input.Search
                  placeholder='Search'
                  allowClear
                  enterButton={<SearchOutlined />}
                  onSearch={setFilterInput}
                />
                <Button
                  type='primary'
                  icon={<UserAddOutlined />}
                  onClick={() => {
                    setIsDrawerOpen(true)
                  }}
                >
                  New workstation
                </Button>
              </Space>
            </Row>
          </Col>
        ]}
      />

      <Table
        size='small'
        tableLayout='auto'
        dataSource={filterData}
        pagination={false}
        loading={isLoading}
        // rowClassName={(record: Participant) => 'disable-row'}
        footer={(data) => (
          <Row justify='start'>
            <span>
              Total: {participantCounts.total} workstations <br /> Active:{' '}
              {participantCounts.active} workstations
              <br /> Deactivated: {participantCounts.deactivated} workstations
            </span>
          </Row>
        )}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a, b) => compareNumber(a.id, b.id)
          },
          {
            title: 'Name',
            render: (v, student) => (
              <>
                <Typography.Text>
                  {student?.first_name} {student?.last_name}
                </Typography.Text>
                <br />
                <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
                  Email: {student?.email}
                </Typography.Text>
                <br />
                <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
                  Username: {student?.username}
                </Typography.Text>
                <br />
                <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
                  Password: {student?.password}
                </Typography.Text>
              </>
            ),
            sorter: (a, b) =>
              compareText(
                `${a.first_name} ${a.last_name}`,
                `${b.first_name} ${b.last_name}`
              )
          },
          {
            title: 'AVW Role',
            render: (student) => student?.avw_role ?? 'User'
          },
          {
            title: 'Appstream',
            render: (student) => (
              <>
                {student.appstream?.stack_name ? (
                  <>
                    {student.appstream.stack_name}
                    <br />
                    {student.reserve_server?.name && (
                      <Typography.Text
                        type='secondary'
                        style={{ fontSize: '12px' }}
                      >
                        Linked server: {student.reserve_server.name}
                      </Typography.Text>
                    )}
                  </>
                ) : student.reserve_server?.name ? (
                  <>Reserved: {student.reserve_server.name}</>
                ) : (
                  ''
                )}
              </>
            ),
            onFilter: (value, record) => {
              if (value === 'unassigned') {
                return record.appstream === null
              }

              return (record.appstream?.stack_name ?? '').includes(
                value.toString()
              )
            },
            filters: appstreamFilters
          },
          {
            title: 'Connected Server',
            filters: serverFilters,
            onFilter: (value, record) => {
              if (value === 'unassigned') {
                return record.server === null
              }

              return (record.server?.name ?? '').includes(value?.toString())
            },
            render: (item) => item.server?.name
          },
          {
            title: 'Passkey',
            render: (item) => {
              if (!item.is_passkey_required) return null
              if (!item.passkey_provider) {
                return <Tag color='orange'>Required</Tag>
              }
              return <Tag color='green'>{item.passkey_provider}</Tag>
            }
          },
          {
            title: 'Actions',
            render: (item) => {
              return (
                <Space size='middle'>
                  <Button
                    key={1}
                    type='primary'
                    icon={<EditOutlined />}
                    onClick={() => {
                      setIsDrawerOpen(true)
                      setEditing(item)
                    }}
                    disabled={item.enabled === false}
                  />
                  {item.join_type == null ||
                    (item.join_type === JOIN_TYPE_HANDS_ON && (
                      <a
                        href={item.workstation_link}
                        target='_blank'
                        rel='noreferrer'
                        key='workstation_link'
                      >
                        <Button
                          type='primary'
                          icon={<DesktopOutlined />}
                          title='Workstation'
                        />
                      </a>
                    ))}
                  <Dropdown
                    key={2}
                    className='dropdown-action'
                    placement='bottom'
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          key: 'Copy Link',
                          label: 'Copy link',
                          disabled: item.enabled === false,
                          onClick: (): void => {
                            clipboard(
                              `${window.location.origin}/workstation-info/${item.appstream_token}`
                            )
                            message.success('Copied')
                          }
                        },
                        {
                          key: 'enable',
                          label: item.enabled === true ? 'Disable' : 'Enable',
                          onClick: () => {
                            confirmEnabled(item)
                          }
                        },
                        {
                          key: 'pre_access',
                          label: `${
                            item.pre_access === true ? 'Disable' : 'Enable'
                          } Pre access`,
                          disabled: item.enabled === false,
                          onClick: () => {
                            confirmPreAccess(item)
                          }
                        },
                        {
                          key: 'reset_passkey',
                          label: 'Reset passkey',
                          disabled: item.enabled === false || !item.has_passkey,
                          onClick: () => {
                            confirmResetPasskey(item)
                          }
                        },
                        {
                          key: 'delete',
                          label: 'Delete',
                          onClick: () => {
                            confirmDelete(item)
                          }
                        }
                      ]
                    }}
                  >
                    <Button type='primary' icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space>
              )
            }
          }
        ]}
      />

      <Drawer
        title='Create/Update Workstation'
        placement='right'
        closable={false}
        size={720}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setEditing(null)
        }}
        destroyOnHidden
      >
        <ParticipantForm
          courseId={Number(courseId)}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: courseParticipantQueryKey(id)
            })
            setIsDrawerOpen(false)
            setEditing(null)
          }}
          item={editing}
        />
      </Drawer>
    </>
  )
}

export default CourseParticipants
